import { findCar, sortCars } from './carCatalogue';
import { filterCars } from './carFilters';

/**
 * The detail screen's deck.
 *
 * A car page is a card in a deck, and a sideways swipe deals the next one. What
 * the deck CONTAINS is the one decision this module makes differently from the
 * sibling product it is modelled on: there the deck is a plant's category, and
 * here it is THE SHELF THE READER ARRIVED FROM — the filtered list, whatever
 * they narrowed it to. Somebody who filtered to 90s JDM and opened the Supra
 * means to swipe to the RX-7; dealing them a Countach takes their shelf away
 * mid-gesture, and they have no way to ask for it back except to go and filter
 * again.
 *
 * That is why the filter state is an ARGUMENT rather than something read from
 * here. The deck is pure and stateless: the car in the URL plus the filters the
 * screen is holding is a complete description of where in the ring the reader
 * is, so a swipe never has to remember anything.
 *
 * A RING, so the last card of a shelf is followed by the first. Browsing that
 * stops is browsing a reader has to back out of, and a shelf filtered to three
 * cars stops very quickly.
 */

/** Index arithmetic on a ring: `-1` is the last card, `length` is the first. */
function wrap(index, length) {
    return ((index % length) + length) % length;
}

/** What a car that is not in the garage — or no car at all — is in. */
export const EMPTY_DECK = {
    cards: [],
    index: -1,
    previous: null,
    next: null,
    total: 0,
    isFiltered: false,
};

/**
 * @param {Array} cars - the joined catalogue.
 * @param {string} carId - the car on screen.
 * @param {object} [filters] - the shelf the reader arrived from. Absent, or
 *   empty, and the deck is the whole garage.
 * @returns {{cards, index, previous, next, total, isFiltered}} `previous` and
 *   `next` wrap around the ring, so neither is ever null in a shelf of two or
 *   more. They are null only where there is genuinely nowhere to go: a shelf of
 *   one, where every step would land back on the card already showing.
 */
export function buildDeck(cars, carId, { filters = null, languageCode = 'en' } = {}) {
    const catalogue = cars || [];
    const car = findCar(catalogue, carId);

    if (!car) {
        return EMPTY_DECK;
    }

    const shelf = filterCars(catalogue, filters);
    // A shared link can carry a filter the car itself fails — the reader followed
    // it from somebody else's filtered garage. Dealing a deck the card on screen
    // is not a member of would make its own index meaningless and every swipe a
    // jump, so the whole garage is the honest fallback.
    const members = shelf.includes(car) ? shelf : catalogue;
    const cards = sortCars(members, languageCode);
    const index = cards.findIndex((candidate) => candidate.id === carId);
    const step = (offset) => (cards.length > 1 ? cards[wrap(index + offset, cards.length)] : null);

    return {
        cards,
        index,
        previous: step(-1),
        next: step(1),
        total: cards.length,
        isFiltered: members !== catalogue,
    };
}

/**
 * Where a released swipe goes, or `null` if it goes nowhere — which now happens
 * only in a shelf of one.
 *
 * The direction words are the gesture's, not the list's: dragging left brings
 * the next card in from the right, which is the direction a page turns forward
 * (`readGesture` in the motion hook says the same thing).
 */
export function deckStep(deck, direction) {
    return (direction === 'back' ? deck.previous : deck.next) ?? null;
}

/**
 * The cards drawn as slivers behind the current one, nearest first.
 *
 * Capped at three, and the first of them is invisible: it sits at exactly the
 * front card's box, because it is the card about to take that box and the
 * picture it already shows must not move when it does. The two behind it are
 * what a reader actually sees of the stack.
 *
 * It follows the ring, so on the last card the cards behind are the first ones
 * again — the honest picture for a deck that wraps: the stack says "there is
 * more of this", which is true everywhere, rather than counting down to an end
 * that does not exist.
 *
 * Never a card twice, and never the card in front: a shelf of two shows one
 * sliver, and a shelf of one shows none.
 *
 * It follows the DIRECTION the reader is going, because the card behind is the
 * card they are about to see. At rest that is forward; mid-gesture it is
 * whichever way the finger went, so the picture waiting under the card being
 * dealt away is the picture of the car actually arriving.
 */
export const MAX_STACK_DEPTH = 3;

export function deckStack(deck, direction = 'forward') {
    const depth = Math.min(MAX_STACK_DEPTH, Math.max(deck.total - 1, 0));
    const towards = direction === 'back' ? -1 : 1;

    return Array.from(
        { length: depth },
        (unused, step) => deck.cards[wrap(deck.index + (step + 1) * towards, deck.total)],
    );
}

/**
 * The deck as SEATS: every card that can be on screen in the next moment, and
 * which role it is playing.
 *
 *   FRONT     the card in hand. Carries the gesture and the forward deal.
 *   REVEAL    the next car, in the front card's exact box, one z below it. A
 *             forward swipe throws the front card off and this is simply there —
 *             it never had to arrive.
 *   INCOMING  the PREVIOUS car, parked off the edge it left by, ABOVE the front
 *             card. A backward swipe pulls it back on top: the deal undone
 *             rather than a second kind of reveal.
 *   PEEK      the edge that says there is a deck here. Draws no picture.
 *
 * One list, keyed by car, because that is the whole mechanism: a step MOVES the
 * node that was behind into the front role rather than rewriting the node in
 * front. Rewriting a live `<img>` tears its frame down and re-attaches the
 * bitmap, and these are large cut-outs — the repaint is visible.
 *
 * Both directions therefore hold a mounted, decoded card at all times, which is
 * what makes reversing mid-browse instant: there is nothing to add when the
 * direction becomes obvious, because both were already there.
 */
export function deckSeats(deck) {
    if (!deck.total) {
        return [];
    }

    const at = (offset) => deck.cards[wrap(deck.index + offset, deck.total)];
    const seats = [{ car: at(0), role: 'front', seatDepth: 0 }];
    const seen = new Set([at(0).id]);

    // Order matters only for the ones that overlap: a car cannot hold two seats,
    // and in a shelf of two the same card is both what is next and what is
    // previous. Reveal wins, because forward is the direction a deck at rest is
    // pointing.
    [
        { car: at(1), role: 'reveal', seatDepth: 1 },
        { car: at(-1), role: 'incoming', seatDepth: -1 },
        { car: at(2), role: 'peek', seatDepth: 2 },
    ].forEach((seat) => {
        if (!seen.has(seat.car.id)) {
            seen.add(seat.car.id);
            seats.push(seat);
        }
    });

    return seats;
}
